import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ActionButtons } from 'components';
import { OptionsMenu } from './OptionsMenu';

/**
 * GigabotHeader - Header component for Gigabot chat panel
 *
 * Props:
 * - onClose: function - Callback when close button is clicked
 * - showHistory: boolean - Whether history panel is shown
 * - onToggleHistory: function - Callback to toggle history panel
 * - showActionProposals: boolean - Whether action proposals feature is enabled
 * - autoRunMode: string - Current auto-run mode ('off', 'turbo')
 * - onAutoRunModeChange: function - Callback when auto-run mode changes
 * - showAutoRunSettings: boolean - Whether auto-run settings menu is open
 * - onToggleAutoRunSettings: function - Callback to toggle auto-run settings
 * - hasActiveConversation: boolean - Whether there's an active conversation
 * - onNewConversation: function - Callback to start a new conversation
 */
export const GigabotHeader = ({
  onClose,
  showHistory,
  onToggleHistory,
  showActionProposals = false,
  autoRunMode = 'off',
  onAutoRunModeChange,
  showAutoRunSettings = false,
  onToggleAutoRunSettings,
  hasActiveConversation = false,
  onNewConversation,
}) => {
  const autoRunSettingsRef = useRef(null);
  const headerActions = useMemo(
    () => [
      ...(hasActiveConversation && onNewConversation
        ? [
            {
              id: 'new-conversation',
              icon: 'Add_Plus',
              onClick: () => onNewConversation(),
              variant: 'primary',
            },
          ]
        : []),
      {
        id: 'toggle-history',
        icon: 'Clock',
        onClick: () => onToggleHistory(),
        variant: showHistory ? 'primary' : 'default',
      },
    ],
    [hasActiveConversation, onNewConversation, onToggleHistory, showHistory]
  );

  const settingsActions = useMemo(
    () => [
      {
        id: 'auto-run-settings',
        icon: 'Settings',
        onClick: () => onToggleAutoRunSettings(),
        variant: showAutoRunSettings ? 'primary' : 'default',
      },
    ],
    [onToggleAutoRunSettings, showAutoRunSettings]
  );

  const closeActions = useMemo(
    () => [
      {
        id: 'close-gigabot',
        icon: 'Close_LG',
        onClick: () => onClose(),
        variant: 'primary',
      },
    ],
    [onClose]
  );

  return (
    <div className='gigabot-header'>
      <div className='gigabot-header-left'>
        <h3>Gigabot</h3>

        <ActionButtons
          actions={headerActions}
          align='left'
          gap='xs'
          iconSize='sm'
        />
      </div>
      <div className='gigabot-header-controls'>
        {showActionProposals && (
          <div className='auto-run-settings' ref={autoRunSettingsRef}>
            <ActionButtons
              actions={settingsActions}
              align='right'
              gap='xs'
              iconSize='sm'
            />

            <OptionsMenu
              isOpen={showAutoRunSettings}
              title='Auto-Run Mode'
              options={[
                { value: 'off', label: 'Off' },
                { value: 'turbo', label: 'Turbo' },
              ]}
              activeValue={autoRunMode}
              onSelect={(value) => onAutoRunModeChange(value)}
              onRequestClose={() => onToggleAutoRunSettings()}
              anchorRef={autoRunSettingsRef}
            />
          </div>
        )}

        <ActionButtons
          actions={closeActions}
          align='right'
          gap='xs'
          iconSize='sm'
        />
      </div>
    </div>
  );
};

GigabotHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
  showHistory: PropTypes.bool,
  onToggleHistory: PropTypes.func.isRequired,
  showActionProposals: PropTypes.bool,
  autoRunMode: PropTypes.oneOf(['off', 'turbo']),
  onAutoRunModeChange: PropTypes.func,
  showAutoRunSettings: PropTypes.bool,
  onToggleAutoRunSettings: PropTypes.func,
  hasActiveConversation: PropTypes.bool,
  onNewConversation: PropTypes.func,
};

export default GigabotHeader;
